

import { useState, useEffect, useCallback, useRef } from 'react';
import MetadataService from '../services/metadataService';
import { useAutomationContext } from '../context/AutomationContext';
import { useAuth } from 'auth/Auth';

// Fields that should NOT be selectable in the Automation wizard
// (system / audit fields)
const HIDDEN_AUDIT_FIELDS = [
  'created_at',
  'updated_at',
  'created_by',
  'updated_by',
];

export function useAutomationMetadata() {
  const { state, actions } = useAutomationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { axiosInstance } = useAuth();
  
  // Use useRef to persist the service instance
  const metadataServiceRef = useRef(null);
  if (!metadataServiceRef.current) {
    metadataServiceRef.current = new MetadataService({ axiosInstance });
  }
  
  // Use ref to track if initial load has happened
  const hasLoadedObjects = useRef(false);

  const loadObjectMetadata = useCallback(async () => {
    // Prevent duplicate loads
    if (isLoading || hasLoadedObjects.current) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const objects = await metadataServiceRef.current.getObjects();
      actions.setMetadata('objectMetadata', 
        objects.reduce((acc, obj) => {
          acc[obj.id] = obj;
          return acc;
        }, {})
      );
      hasLoadedObjects.current = true;
      return objects;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [actions, isLoading]); // Remove state dependencies

  const loadFieldMetadata = useCallback(async (objectType) => {
    if (!objectType) return [];
    
    try {
      actions.setLoading({ fields: true });
      const fields = await metadataServiceRef.current.getFields(objectType);
      
      // Use functional update to avoid dependency on state
      actions.setMetadata('fieldMetadata', (prevFieldMetadata) => ({
        ...prevFieldMetadata,
        [objectType]: fields
      }));
      
      return fields;
    } catch (err) {
      console.error(`Error loading fields for ${objectType}:`, err);
      return [];
    } finally {
      actions.setLoading({ fields: false });
    }
  }, [actions]); // Remove state.fieldMetadata dependency

  const loadRelationships = useCallback(async (objectType) => {
    if (!objectType) return {};
    
    // Check if we already tried and failed
    const failedKey = `relationships_failed_${objectType}`;
    if (state[failedKey]) return {};
    
    try {
      const relationships = await metadataServiceRef.current.getRelationships(objectType);
      
      actions.setMetadata('relationships', (prevRelationships) => ({
        ...prevRelationships,
        [objectType]: relationships
      }));
      
      return relationships;
    } catch (err) {
      console.error(`Error loading relationships for ${objectType}:`, err);
      // Mark as failed to prevent retries
      actions.setMetadata(failedKey, true);
      // Return empty object for relationships
      actions.setMetadata('relationships', (prevRelationships) => ({
        ...prevRelationships,
        [objectType]: {}
      }));
      return {};
    }
  }, [actions]);

  const loadOperators = useCallback(async (fieldType) => {
    if (!fieldType) return [];
    
    try {
      const operators = await metadataServiceRef.current.getOperators(fieldType);
      
      // Use functional update
      actions.setMetadata('operators', (prevOperators) => ({
        ...prevOperators,
        [fieldType]: operators
      }));
      
      return operators;
    } catch (err) {
      console.error(`Error loading operators for ${fieldType}:`, err);
      return [];
    }
  }, [actions]); // Remove state.operators dependency

  const getObjectCapabilities = useCallback((objectType) => {
    return state.objectMetadata[objectType]?.capabilities || {};
  }, [state.objectMetadata]);

  const getFieldsForObject = useCallback((objectType) => {
    const allFields = state.fieldMetadata[objectType] || [];

    // Hide system/audit fields from selection in Conditions / Actions
    return allFields.filter((field) => !HIDDEN_AUDIT_FIELDS.includes(field.id));
  }, [state.fieldMetadata]);

  const getRelatedObjects = useCallback((objectType) => {
    const relationships = state.relationships[objectType] || {};
    return [
      ...(relationships.children || []),
      ...(relationships.parents || []),
      ...(relationships.lookups || [])
    ];
  }, [state.relationships]);

  // Initial load - only once
  useEffect(() => {
    if (!hasLoadedObjects.current) {
      loadObjectMetadata();
    }
  }, []); // Empty dependency array for one-time load

  // Load fields and relationships when object changes
  useEffect(() => {
    if (state.wizardData.object) {
      loadFieldMetadata(state.wizardData.object);
      loadRelationships(state.wizardData.object);
    }
  }, [state.wizardData.object]); // ← Only depend on the object changing!

  return {
    isLoading,
    error,
    loadObjectMetadata,
    loadFieldMetadata,
    loadRelationships,
    loadOperators,
    getObjectCapabilities,
    getFieldsForObject,
    getRelatedObjects,
    metadata: {
      objects: state.objectMetadata,
      fields: state.fieldMetadata,
      relationships: state.relationships,
      operators: state.operators
    }
  };
}
